'use client';

import { MediaImageList } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Image as DatoImage } from 'react-datocms/image';
import styles from './ImageGallery.module.css';
import type { ImageGalleryDatoImage, ImageGalleryProps, ImageGallerySource } from './ImageGallery.types';
import { ImageGalleryCarouselModal } from './ImageGalleryCarouselModal';

const isDatoSource = (image: ImageGallerySource): image is ImageGalleryDatoImage =>
  'data' in image && image.data != null;

const hasValidSrc = (image: ImageGallerySource) =>
  isDatoSource(image) ? Boolean(image.data?.src) : Boolean(image.src);

const imageSourceKey = (image: ImageGallerySource, index: number) =>
  `${isDatoSource(image) ? image.data.src : image.src}-${index}`;

const altForSource = (image: ImageGallerySource) => (isDatoSource(image) ? (image.data.alt ?? '') : (image.alt ?? ''));

const creditForSource = (image: ImageGallerySource): string | undefined => {
  if (!isDatoSource(image)) return image.credit ?? undefined;
  return typeof image.data.title === 'string' ? image.data.title : undefined;
};

const renderCredit = (image: ImageGallerySource) => {
  const credit = creditForSource(image);
  if (!credit) return null;
  return (
    <>
      <div className={styles.imageCreditGradient} aria-hidden="true" />
      <p className={styles.imageCredit}>{credit}</p>
    </>
  );
};

const renderGalleryImage = (image: ImageGallerySource, className: string, loading: 'eager' | 'lazy' = 'lazy') =>
  isDatoSource(image) ? (
    <DatoImage data={image.data} className={className} layout="fill" objectFit="cover" priority={loading === 'eager'} />
  ) : (
    <img
      src={image.src}
      alt={altForSource(image)}
      className={className}
      loading={loading}
      fetchPriority={loading === 'eager' ? 'high' : 'auto'}
    />
  );

export const ImageGallery = ({ images: imagesProp, title, titleId }: ImageGalleryProps) => {
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tAria = useTranslations('aria');

  const galleryTitle = title ?? tAria('image_gallery_default_title');

  const sources = (imagesProp ?? []).filter(hasValidSrc);
  const [heroImage, ...teaserImages] = sources.slice(0, 5);

  if (!sources.length) return null;

  const hasDesktopTeasers = teaserImages.length >= 3;

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    if (clientWidth > 0) setSlideIndex(Math.round(scrollLeft / clientWidth));
  };

  return (
    <>
      {titleId ? (
        <h2 id={titleId} className={styles.srOnly}>
          {galleryTitle}
        </h2>
      ) : null}
      <div className={styles.wrapper} data-test-id="ImageGallery" data-image-gallery>
        <div className={styles.relative}>
          <div className={styles.grid}>
            <div className={clsx(hasDesktopTeasers ? styles.hero : styles.heroFullWidth)}>
              {renderGalleryImage(heroImage, styles.heroImage, 'eager')}
              {renderCredit(heroImage)}
            </div>
            {hasDesktopTeasers &&
              teaserImages.slice(0, 3).map((image, index) => (
                <div key={imageSourceKey(image, index)} className={styles.gridCell}>
                  {renderGalleryImage(image, styles.gridImage)}
                  {renderCredit(image)}
                </div>
              ))}
          </div>

          <section className={styles.carousel} aria-roledescription={tAria('carousel')} aria-label={galleryTitle}>
            <div ref={scrollRef} className={styles.scrollTrack} onScroll={handleScroll}>
              {sources.map((image, index) => (
                <div key={`${imageSourceKey(image, index)}-carousel`} className={styles.slide}>
                  {renderGalleryImage(image, styles.slideImage, index === 0 ? 'eager' : 'lazy')}
                  {renderCredit(image)}
                </div>
              ))}
            </div>
            {sources.length > 1 && (
              <ul className={styles.dots} aria-label={tAria('card_gallery_slides')}>
                {sources.map((image, i) => (
                  <li key={imageSourceKey(image, i)}>
                    <button
                      type="button"
                      className={clsx(styles.dot, i === slideIndex && styles.dotActive)}
                      aria-label={tAria('card_gallery_go_to_slide', { slide: i + 1 })}
                      aria-current={i === slideIndex ? 'true' : undefined}
                      onClick={() => {
                        if (!scrollRef.current) return;
                        scrollRef.current.scrollTo({ left: i * scrollRef.current.clientWidth, behavior: 'smooth' });
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className={styles.seeAllButton}>
            <button
              type="button"
              className={styles.seeAllChipButton}
              onClick={() => setIsOverlayActive(true)}
              data-test-id="show-all-photos-button"
              aria-label={tAria('image_gallery_show_all')}
            >
              <span className={styles.seeAllChipContent}>
                <MediaImageList decorative isInline size="small" />
                <span className={styles.seeAllChipLabel}>{tAria('image_gallery_show_all')}</span>
              </span>
            </button>
          </div>
        </div>

        <ImageGalleryCarouselModal
          images={sources}
          isOpen={isOverlayActive}
          onClose={() => setIsOverlayActive(false)}
          ariaLabelCarousel={galleryTitle}
        />
      </div>
    </>
  );
};

export type { ImageGalleryDatoImage, ImageGalleryProps, ImageGallerySource } from './ImageGallery.types';
export { ImageGalleryCarouselModal } from './ImageGalleryCarouselModal';
export type { CarouselModalImage, ImageGalleryCarouselModalProps } from './ImageGalleryCarouselModal.types';
